/**
 * H①-native — pure client-side normalizer for the health-sync upload.
 *
 * The server (`POST /api/health-metrics/daily`, api/lib/health/dailyMetrics.js)
 * rejects the WHOLE batch if any day has zero metrics or an unknown key.
 * Mirror that contract before upload so one stray day can't 400 the sync:
 * keep only well-formed YYYY-MM-DD days, copy ONLY the four known metrics
 * (dropping non-finite values), and drop any day left with no metrics.
 *
 * Pure by design (no Capacitor/DOM) so it's unit-testable — the native
 * bridge lives in healthKit.ts.
 */

export interface RawHealthDay {
  day: string;
  activeEnergyKcal?: number | null;
  sleepMinutes?: number | null;
  restingHr?: number | null;
  hrvMs?: number | null;
}

export interface UploadHealthDay {
  day: string;
  activeEnergyKcal?: number;
  sleepMinutes?: number;
  restingHr?: number;
  hrvMs?: number;
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const METRIC_KEYS = ['activeEnergyKcal', 'sleepMinutes', 'restingHr', 'hrvMs'] as const;

export function cleanDaysForUpload(days: readonly RawHealthDay[] | null | undefined): UploadHealthDay[] {
  const out: UploadHealthDay[] = [];
  for (const d of days ?? []) {
    if (!d || typeof d.day !== 'string' || !DAY_RE.test(d.day)) continue;
    const row: UploadHealthDay = { day: d.day };
    let metricCount = 0;
    for (const key of METRIC_KEYS) {
      const v = d[key];
      if (typeof v === 'number' && Number.isFinite(v)) {
        row[key] = v;
        metricCount++;
      }
    }
    if (metricCount > 0) out.push(row);
  }
  return out;
}
